import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { X } from 'lucide-react';
import { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useShortcutScope } from '../keys/runtime.js';
import { cx, useViewer } from './primitives.js';
/**
 * Modal sheet: right-side panel on desktop (`side`) or a centred dialog. Nothing opens from the
 * bottom of the page (user direction): `bottom` and narrow-screen `side`/`auto` render centred.
 * Replaces nested scrollers (docs/ux → Mobile ergonomics). Focus moves into the sheet and
 * returns on close; Esc closes. Portals into the viewer's sheet host (device frames in the
 * gallery) or document.body.
 */
export function Sheet({ open, onClose, title, subtitle, children, actions, placement = 'auto', size = 'md', inline = false, shortcutScope, }) {
    const { sheetHost, wide } = useViewer();
    const panel = useRef(null);
    const titleId = useId();
    // Callers pass fresh onClose closures every render; the focus/Esc effect must not re-run for them
    // (each re-run would return focus to the opener and pull it back to the panel).
    const closeRef = useRef(onClose);
    closeRef.current = onClose;
    // A modal owns the keyboard: page and composer keys are suspended behind it.
    useShortcutScope(shortcutScope ?? 'dialog:generic', open && !inline);
    // The invoker is read while rendering the opening frame: by the time the effect runs, an
    // autoFocus'd field inside the sheet already holds focus and the opener would be lost.
    const opener = useRef(null);
    const wasOpen = useRef(false);
    if (open && !wasOpen.current && typeof document !== 'undefined')
        opener.current = document.activeElement;
    wasOpen.current = open;
    useEffect(() => {
        if (!open || inline)
            return;
        const prev = opener.current;
        // An autoFocus'd field inside the sheet keeps focus (keyboard users start typing at once).
        if (!panel.current?.contains(document.activeElement))
            panel.current?.focus();
        const onKey = (e) => {
            if (e.key === 'Escape' && !e.defaultPrevented)
                closeRef.current();
        };
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('keydown', onKey);
            // Back to the row or button that opened it (not <body>), when it is still on the page.
            if (prev?.isConnected && prev !== document.body)
                prev.focus?.();
        };
    }, [open, inline]);
    if (!open)
        return null;
    const place = (placement === 'auto' || placement === 'side') && wide ? 'side' : 'center';
    const node = (_jsxs("div", { className: cx('vk-sheet', `vk-sheet--${place}`, `vk-sheet--${size}`, inline && 'vk-sheet--inline'), children: [_jsx("button", { type: "button", className: "vk-sheet__scrim", "aria-label": "Close", tabIndex: -1, onClick: onClose }), _jsxs("div", { ref: panel, className: "vk-sheet__panel", role: "dialog", "aria-modal": !inline, "aria-labelledby": titleId, tabIndex: -1, children: [_jsxs("header", { className: "vk-sheet__head", children: [_jsxs("div", { className: "vk-sheet__titles", children: [_jsx("h2", { id: titleId, className: "vk-sheet__title", children: title }), subtitle ? _jsx("div", { className: "vk-sheet__subtitle", children: subtitle }) : null] }), actions, _jsx("button", { type: "button", className: "vk-iconbtn vk-iconbtn--lg", "aria-label": "Close", onClick: onClose, children: _jsx(X, { size: 18 }) })] }), _jsx("div", { className: "vk-sheet__body", children: children })] })] }));
    if (inline)
        return node;
    const host = sheetHost ?? (typeof document !== 'undefined' ? document.body : null);
    return host ? createPortal(node, host) : node;
}
//# sourceMappingURL=Sheet.js.map